<script setup>
import { computed, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useMapGetter, useStore } from 'dashboard/composables/store';
import { corDoNome, iniciaisDoNome } from 'dashboard/helper/avatar';
import EsqueletoLista from 'dashboard/components-next/EsqueletoLista.vue';
import EstadoVazio from 'dashboard/components-next/EstadoVazio.vue';
import BulkPopover from './BulkPopover.vue';

const props = defineProps({
  selectedInboxes: { type: Array, default: () => [] },
  conversationCount: { type: Number, default: 0 },
});

const emit = defineEmits(['select', 'close']);

const { t } = useI18n();
const store = useStore();
const uiFlags = useMapGetter('bulkActions/getUIFlags');
const carregandoFlags = useMapGetter('inboxAssignableAgents/getUIFlags');

const query = ref('');
const escolhido = ref(null);
const falhou = ref(false);

const agentes = computed(() =>
  store.getters['inboxAssignableAgents/getAssignableAgents'](
    props.selectedInboxes.join(',')
  )
);

const filtrados = computed(() => {
  const termo = query.value.trim().toLowerCase();
  return agentes.value.filter(a => a.name.toLowerCase().includes(termo));
});

// `id: null` tira o responsável. O nome era "None", em inglês.
const ninguem = computed(() => ({
  id: null,
  name: t('BULK_ACTION.AGENTS.NONE'),
}));

// A busca do store RELANÇA a falha: sem este `catch` ela virava rejeição
// solta, e o painel ficava vazio como se a caixa não tivesse ninguém.
const buscar = async () => {
  falhou.value = false;
  try {
    await store.dispatch('inboxAssignableAgents/fetch', props.selectedInboxes);
  } catch {
    falhou.value = true;
  }
};

onMounted(buscar);

const confirmacao = computed(() => {
  const n = props.conversationCount;
  if (!escolhido.value) return '';
  return escolhido.value.id
    ? t(
        'BULK_ACTION.ASSIGN_CONFIRM',
        { count: n, agent: escolhido.value.name },
        n
      )
    : t('BULK_ACTION.UNASSIGN_CONFIRM', { count: n }, n);
});
</script>

<template>
  <BulkPopover
    :titulo="t('BULK_ACTION.AGENT_SELECT_LABEL')"
    @close="emit('close')"
  >
    <EsqueletoLista
      v-if="carregandoFlags.isFetching"
      class="bag-esqueleto"
      :linhas="4"
      :altura="36"
      :rotulo="t('BULK_ACTION.AGENT_LIST_LOADING')"
    />

    <EstadoVazio
      v-else-if="falhou"
      class="bag-vazio"
      tom="erro"
      icone="i-lucide-wifi-off"
      :titulo="t('BULK_ACTION.AGENTS.LOAD_ERROR')"
    >
      <button
        type="button"
        class="advup-btn advup-btn--compacto"
        @click="buscar"
      >
        <i class="i-lucide-rotate-cw" aria-hidden="true" />
        {{ t('BULK_ACTION.AGENTS.RETRY') }}
      </button>
    </EstadoVazio>

    <!-- Quem atribui em massa CONFERE antes: são várias conversas trocando
         de dono num clique. -->
    <div v-else-if="escolhido" class="bag-confirma">
      <span
        v-if="escolhido.id"
        class="advup-avatar advup-avatar--g"
        :class="`advup-avatar--${corDoNome(escolhido.name)}`"
        aria-hidden="true"
      >
        {{ iniciaisDoNome(escolhido.name) }}
        <img v-if="escolhido.thumbnail" :src="escolhido.thumbnail" alt="" />
      </span>
      <span v-else class="bag-confirma__ninguem" aria-hidden="true">
        <i class="i-lucide-user-round-x" />
      </span>
      <p class="bag-confirma__txt">{{ confirmacao }}</p>
      <div class="bag-confirma__acoes">
        <button
          type="button"
          class="advup-btn advup-btn--compacto"
          @click="escolhido = null"
        >
          {{ t('BULK_ACTION.GO_BACK_LABEL') }}
        </button>
        <button
          type="button"
          class="advup-btn-forte advup-btn--compacto"
          :disabled="uiFlags.isUpdating"
          @click="emit('select', escolhido)"
        >
          <i
            :class="
              uiFlags.isUpdating
                ? 'i-lucide-loader-2 advup-gira'
                : 'i-lucide-check'
            "
            aria-hidden="true"
          />
          {{ t('BULK_ACTION.CONFIRM') }}
        </button>
      </div>
    </div>

    <template v-else>
      <label class="advup-busca advup-busca--bloco bag-busca">
        <i class="i-lucide-search advup-busca__lupa" aria-hidden="true" />
        <input
          v-model="query"
          type="search"
          class="advup-busca__campo reset-base"
          :placeholder="t('BULK_ACTION.SEARCH_INPUT_PLACEHOLDER')"
          :aria-label="t('BULK_ACTION.SEARCH_INPUT_PLACEHOLDER')"
        />
      </label>
      <div class="bag-lista">
        <template v-if="!query">
          <button
            type="button"
            class="advup-menu__item bag-ninguem"
            @click="escolhido = ninguem"
          >
            <i
              class="i-lucide-user-round-x bag-ninguem__ico"
              aria-hidden="true"
            />
            {{ ninguem.name }}
          </button>
          <div class="advup-menu__filete" />
        </template>
        <button
          v-for="agente in filtrados"
          :key="agente.id"
          type="button"
          class="advup-menu__item"
          @click="escolhido = agente"
        >
          <span
            class="advup-avatar advup-avatar--p"
            :class="`advup-avatar--${corDoNome(agente.name)}`"
            aria-hidden="true"
          >
            {{ iniciaisDoNome(agente.name) }}
            <img v-if="agente.thumbnail" :src="agente.thumbnail" alt="" />
          </span>
          <span class="bag-nome">{{ agente.name }}</span>
        </button>
        <p v-if="!filtrados.length" class="advup-nota bag-nada">
          {{ t('BULK_ACTION.AGENTS.NO_AGENTS_FOUND') }}
        </p>
      </div>
    </template>
  </BulkPopover>
</template>

<style scoped>
.bag-esqueleto {
  padding: 0 var(--esp-2) var(--esp-2);
}

.bag-vazio {
  padding: var(--esp-3);
}

.bag-busca {
  flex-shrink: 0;
  margin: 0 var(--esp-2) var(--esp-1);
}

.bag-lista {
  min-height: 0;
  overflow-y: auto;
}

.bag-ninguem {
  color: rgb(var(--slate-11));
}

.bag-ninguem__ico {
  flex-shrink: 0;
  font-size: var(--ico-2);
}

.bag-nome {
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.bag-nada {
  margin: 0;
  padding: var(--esp-3);
  text-align: center;
}

.bag-confirma {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--esp-3);
  padding: var(--esp-2) var(--esp-3) var(--esp-3);
  text-align: center;
  animation: advupPop var(--mov-2) var(--mov-curva-pulo) backwards;
}

.bag-confirma__ninguem {
  display: grid;
  place-items: center;
  width: 40px;
  height: 40px;
  border-radius: var(--r-redondo);
  background: rgb(var(--slate-3));
  color: rgb(var(--slate-11));
  font-size: var(--ico-3);
}

.bag-confirma__txt {
  margin: 0;
  font-size: var(--txt-corpo);
  line-height: var(--linha-solta);
  color: rgb(var(--slate-12));
}

.bag-confirma__acoes {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: var(--esp-2);
  width: 100%;
}

@media (prefers-reduced-motion: reduce) {
  .bag-confirma {
    animation: none;
  }
}
</style>
